import { Injectable } from '@angular/core';
import {
  Category,
  CategoryWithSources,
  DeclineCategoryRow,
  DeclinePortal,
  DeclineReasonMappingCell,
  DeclineReasonSettingsBatchBody,
  GroupedSettingsResponse,
  OriginalSettingSnapshot,
} from '../interfaces/decline-reason-mapping.interface';

const PORTAL_COLORS = ['#4f9cf9', '#f97316', '#22c55e', '#a855f7', '#ec4899', '#14b8a6', '#f59e0b'];

export interface LoadAllData {
  categories: CategoryWithSources[];
  settings: GroupedSettingsResponse;
  ehrNames: string[];
}

@Injectable()
export class DeclineMappingStateService {
  rows: DeclineCategoryRow[] = [];
  portals: DeclinePortal[] = [];
  portalReasons: Record<string, string[]> = {};
  totalMappedCount = 0;

  activeCell: DeclineReasonMappingCell | null = null;
  activeRow: DeclineCategoryRow | null = null;
  activePortal: DeclinePortal | null = null;
  activePortalReasons: string[] = [];
  activeSelectedReasons: string[] = [];
  activeSelectedReasonSet = new Set<string>();
  activeTakenSet = new Set<string>();
  activeTakenByReason: Record<string, string> = {};

  editingRowId: string | null = null;
  editDraft = '';

  private rowById = new Map<string, DeclineCategoryRow>();
  private rowMappedCounts: Record<string, number> = {};
  private portalById = new Map<string, DeclinePortal>();
  private originalMappings: Record<string, Record<string, OriginalSettingSnapshot>> = {};
  private reasonIndex = new Map<string, Map<string, string>>();
  private nextRowUid = 1;

  initialize({ categories, settings, ehrNames }: LoadAllData): void {
    this.portals = ehrNames.map((name, index) => ({
      id: name,
      name,
      dotColor: PORTAL_COLORS[index % PORTAL_COLORS.length],
    }));
    this.portalById = new Map(this.portals.map((portal) => [portal.id, portal]));

    this.portalReasons = {};
    for (const [srcType, items] of Object.entries(settings)) {
      this.portalReasons[srcType] = items.map((item) => item.declineReason);
    }

    this.originalMappings = {};
    for (const [srcType, items] of Object.entries(settings)) {
      this.originalMappings[srcType] = {};
      for (const item of items) {
        this.originalMappings[srcType][item.declineReason] = {
          settingId: item._id,
          categoryId: item.categoryId,
        };
      }
    }

    this.rows = categories.map((category) => ({
      id: category._id,
      name: category.name,
      mappings: { ...category.sources },
    }));
    this.rebuildRowCaches();
    this.syncActivePanelState();
  }

  rowMappedCount(categoryRow: DeclineCategoryRow): number {
    return this.rowMappedCounts[categoryRow.id] ?? 0;
  }

  isCellActive(rowId: string, portalId: string): boolean {
    return !!this.activeCell && this.activeCell.rowId === rowId && this.activeCell.portalId === portalId;
  }

  setActiveCell(cell: DeclineReasonMappingCell | null): void {
    this.activeCell = cell;
    this.syncActivePanelState();
  }

  getRowById(id: string): DeclineCategoryRow | undefined {
    return this.rowById.get(id);
  }

  toggleReason(rowId: string, portalId: string, reason: string): void {
    const categoryRow = this.rowById.get(rowId);
    if (!categoryRow) return;
    const updatedReasons = [...(categoryRow.mappings[portalId] ?? [])];
    const existingIndex = updatedReasons.indexOf(reason);
    if (existingIndex >= 0) {
      updatedReasons.splice(existingIndex, 1);
    } else {
      updatedReasons.push(reason);
    }
    categoryRow.mappings[portalId] = updatedReasons;
    this.rebuildRowCaches();
    this.syncActivePanelState();
  }

  removeReason(categoryRow: DeclineCategoryRow, portalId: string, reason: string): void {
    categoryRow.mappings[portalId] = (categoryRow.mappings[portalId] ?? []).filter(
      (existingReason) => existingReason !== reason,
    );
    this.rebuildRowCaches();
    this.syncActivePanelState();
  }

  /** Creates a new temporary row, sets it into edit mode, and returns it. */
  addTempRow(): DeclineCategoryRow {
    const id = 'temp_' + this.nextRowUid++;
    const mappings: Record<string, string[]> = {};
    for (const portal of this.portals) {
      mappings[portal.id] = [];
    }
    const newCategoryRow: DeclineCategoryRow = { id, name: '', mappings };
    this.rows = [...this.rows, newCategoryRow];
    this.rebuildRowCaches();
    this.editingRowId = id;
    this.editDraft = '';
    this.activeCell = null;
    this.syncActivePanelState();
    return newCategoryRow;
  }

  /** Replaces a temp row with the persisted row returned from the API. */
  promoteRow(tempId: string, created: Category): void {
    this.rows = this.rows.map((categoryRow) =>
      categoryRow.id === tempId ? { ...categoryRow, id: created._id, name: created.name } : categoryRow,
    );
    if (this.activeCell?.rowId === tempId) {
      this.activeCell = { ...this.activeCell, rowId: created._id };
    }
    this.editingRowId = null;
    this.rebuildRowCaches();
    this.syncActivePanelState();
  }

  startEdit(categoryRow: DeclineCategoryRow): void {
    this.editingRowId = categoryRow.id;
    this.editDraft = categoryRow.name;
  }

  updateRowAfterEdit(categoryRow: DeclineCategoryRow, updated: Category): void {
    categoryRow.name = updated.name;
    this.editingRowId = null;
    this.rebuildRowCaches();
    this.syncActivePanelState();
  }

  clearEditing(): void {
    this.editingRowId = null;
  }

  cleanupRowLocally(rowId: string): void {
    this.rows = this.rows.filter((categoryRow) => categoryRow.id !== rowId);
    if (this.activeCell?.rowId === rowId) this.activeCell = null;
    if (this.editingRowId === rowId) this.editingRowId = null;
    this.rebuildRowCaches();
    this.syncActivePanelState();
  }

  removeRowByRef(targetRow: DeclineCategoryRow): void {
    this.rows = this.rows.filter((categoryRow) => categoryRow !== targetRow);
    this.rebuildRowCaches();
    this.syncActivePanelState();
  }

  computeMappingDiff(): DeclineReasonSettingsBatchBody {
    const added: NonNullable<DeclineReasonSettingsBatchBody['added']> = [];
    const removed: NonNullable<DeclineReasonSettingsBatchBody['removed']> = [];

    for (const [srcType, reasons] of Object.entries(this.portalReasons)) {
      for (const declineReason of reasons) {
        const originalSetting = this.originalMappings[srcType]?.[declineReason];
        const originalCatId = originalSetting?.categoryId ?? null;
        const currentCatId = this.findCategoryIdForReason(srcType, declineReason);

        if (originalCatId !== currentCatId) {
          if (originalSetting?.settingId && currentCatId) {
            added.push({ settingId: originalSetting.settingId, categoryId: currentCatId });
          } else if (originalSetting?.settingId && originalCatId) {
            removed.push({ settingId: originalSetting.settingId });
          }
        }
      }
    }

    return {
      ...(added.length ? { added } : {}),
      ...(removed.length ? { removed } : {}),
    };
  }

  applyMappingSnapshot(): void {
    for (const [srcType, reasons] of Object.entries(this.portalReasons)) {
      if (!this.originalMappings[srcType]) {
        this.originalMappings[srcType] = {};
      }
      for (const declineReason of reasons) {
        const existingSnapshot = this.originalMappings[srcType][declineReason];
        if (!existingSnapshot?.settingId) continue;
        this.originalMappings[srcType][declineReason] = {
          settingId: existingSnapshot.settingId,
          categoryId: this.findCategoryIdForReason(srcType, declineReason),
        };
      }
    }
  }

  private findCategoryIdForReason(srcType: string, reason: string): string | null {
    return this.reasonIndex.get(srcType)?.get(reason) ?? null;
  }

  private rebuildRowCaches(): void {
    this.rowById = new Map<string, DeclineCategoryRow>();
    this.rowMappedCounts = {};
    this.reasonIndex = new Map();
    this.totalMappedCount = 0;

    for (const categoryRow of this.rows) {
      this.rowById.set(categoryRow.id, categoryRow);
      let mappedCount = 0;
      for (const [srcType, reasons] of Object.entries(categoryRow.mappings)) {
        mappedCount += reasons?.length ?? 0;
        if (reasons?.length) {
          let reasonToRowIdMap = this.reasonIndex.get(srcType);
          if (!reasonToRowIdMap) {
            reasonToRowIdMap = new Map();
            this.reasonIndex.set(srcType, reasonToRowIdMap);
          }
          for (const reason of reasons) {
            reasonToRowIdMap.set(reason, categoryRow.id);
          }
        }
      }
      this.rowMappedCounts[categoryRow.id] = mappedCount;
      this.totalMappedCount += mappedCount;
    }
  }

  private syncActivePanelState(): void {
    if (!this.activeCell) {
      this.activeRow = null;
      this.activePortal = null;
      this.activePortalReasons = [];
      this.activeSelectedReasons = [];
      this.activeSelectedReasonSet = new Set<string>();
      this.activeTakenSet = new Set<string>();
      this.activeTakenByReason = {};
      return;
    }

    const selectedCategoryRow = this.rowById.get(this.activeCell.rowId) ?? null;
    const selectedPortal = this.portalById.get(this.activeCell.portalId) ?? null;

    this.activeRow = selectedCategoryRow;
    this.activePortal = selectedPortal;
    this.activePortalReasons = this.portalReasons[this.activeCell.portalId] ?? [];
    this.activeSelectedReasons = selectedCategoryRow?.mappings[this.activeCell.portalId] ?? [];
    this.activeSelectedReasonSet = new Set(this.activeSelectedReasons);
    this.activeTakenSet = new Set<string>();
    this.activeTakenByReason = {};

    for (const otherCategoryRow of this.rows) {
      if (otherCategoryRow.id === this.activeCell.rowId) continue;
      for (const reason of otherCategoryRow.mappings[this.activeCell.portalId] ?? []) {
        this.activeTakenSet.add(reason);
        this.activeTakenByReason[reason] = otherCategoryRow.name;
      }
    }
  }
}
